var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useContext } from "react";
import Keycloak from "keycloak-js";
import { createContext, useEffect, useState } from "react";
import { getValueFromCookie, sleep, toBCP47, USER_LANGUAGE_COOKIE_NAME } from "@sequentech/ui-core";
import { SettingsContext } from "./SettingsContextProvider";
import { getLanguageFromURL } from "../utils/queryParams";
import { useTranslation } from "react-i18next";
import { IPermissions } from "../types/keycloak";
import { appendLoginHints, enrollmentRedirectUrl } from "../utils/loginHints";
import { getLogoutRedirectUrl, isKioskClientId } from "../utils/logoutRedirect";
import { getKioskAwareUrl, getKioskPortalRedirectUrl } from "../utils/kioskUrls";
/**
 * Default values for the {@link AuthContext}
 */
var defaultAuthContextValues = {
    isAuthContextInitialized: false,
    isAuthenticated: false,
    userId: "",
    username: "",
    email: "",
    firstName: "",
    keycloakAccessToken: undefined,
    logout: function () { },
    getExpiry: function () { return undefined; },
    setTenantEvent: function (_tenantId, _eventId, _authType, _defaultLocale, _loginHints) { },
    hasRole: function () { return false; },
    isKiosk: function () { return false; },
    openProfileLink: function () { return new Promise(function () { return undefined; }); },
    isGoldUser: function () { return false; },
    reauthWithGold: function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
        return [2 /*return*/];
    }); }); },
};
/**
 * Create the AuthContext using the default values.
 */
export var AuthContext = createContext(defaultAuthContextValues);
/**
 * AuthContextProvider is responsible for managing the authentication state of the current user.
 *
 * @param props
 */
var AuthContextProvider = function (props) {
    var _a, _b, _c, _d;
    var _e = useContext(SettingsContext), loaded = _e.loaded, globalSettings = _e.globalSettings;
    var _f = useState(), keycloak = _f[0], setKeycloak = _f[1];
    var _g = useState(false), isKeycloakInitialized = _g[0], setIsKeycloakInitialized = _g[1];
    var _h = useState(), keycloakAccessToken = _h[0], setKeycloakAccessToken = _h[1];
    // Create the local state in which we will keep track if a user is authenticated
    var _j = useState(false), isAuthenticated = _j[0], setAuthenticated = _j[1];
    // Local state that will contain the users name once it is loaded
    var _k = useState(null), userProfile = _k[0], setUserProfile = _k[1];
    var _l = useState(null), tenantId = _l[0], setTenantId = _l[1];
    var _m = useState(null), eventId = _m[0], setEventId = _m[1];
    var _o = useState(null), authType = _o[0], setAuthType = _o[1];
    var _p = useState(undefined), defaultLocale = _p[0], setDefaultLocale = _p[1];
    var _q = useState({}), loginHints = _q[0], setLoginHints = _q[1];
    var i18n = useTranslation().i18n;
    useEffect(function () {
        function createKeycloak() {
            var _this = this;
            if (keycloak || !tenantId || !eventId) {
                return;
            }
            var isKiosk = new URLSearchParams(window.location.search).has("kiosk");
            var kioskPortalRedirectUrl = getKioskPortalRedirectUrl(window.location.href, globalSettings.KIOSK_VOTING_PORTAL_URL, isKiosk);
            if (kioskPortalRedirectUrl) {
                window.location.replace(kioskPortalRedirectUrl);
                return;
            }
            /**
             * Get the voting client. If there's a param `kiosk` in the URL, it
             * append `-kiosk` to the
             */
            var getClientId = function (defaultClientId) {
                return isKiosk ? "".concat(defaultClientId, "-kiosk") : defaultClientId;
            };
            /**
             * KeycloakConfig configures the connection to the Keycloak server.
             */
            var createKeycloakConfig = function (tenantId, eventId, keycloakUrl, clientId) {
                return {
                    realm: "tenant-".concat(tenantId, "-event-").concat(eventId),
                    url: keycloakUrl,
                    clientId: clientId,
                };
            };
            var keycloakConfig = createKeycloakConfig(tenantId, eventId, getKioskAwareUrl(globalSettings.KEYCLOAK_URL, globalSettings.KIOSK_KEYCLOAK_URL, isKiosk), getClientId(globalSettings.ONLINE_VOTING_CLIENT_ID));
            // Create the Keycloak client instance
            var newKeycloak = new Keycloak(keycloakConfig);
            newKeycloak.onTokenExpired = function () { return __awaiter(_this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    /*const refreshed = await newKeycloak.updateToken(0)
    
                    if (refreshed) {
                        setKeycloakAccessToken(newKeycloak.token)
                    } else {
                        newKeycloak.logout()
                    }*/
                    newKeycloak.logout({
                        redirectUri: getLogoutRedirectUrl({
                            origin: window.location.origin,
                            pathname: window.location.pathname,
                            tenantId: tenantId,
                            eventId: eventId,
                            clientId: newKeycloak.clientId,
                            defaultClientId: globalSettings.ONLINE_VOTING_CLIENT_ID,
                            hasKioskQuery: new URLSearchParams(window.location.search).has("kiosk"),
                        }),
                    });
                    return [2 /*return*/];
                });
            }); };
            setKeycloak(newKeycloak);
        }
        if (!keycloak && loaded && tenantId && eventId) {
            createKeycloak();
        }
    }, [
        tenantId,
        eventId,
        keycloak,
        setKeycloak,
        loaded,
        globalSettings.KEYCLOAK_URL,
        globalSettings.KIOSK_KEYCLOAK_URL,
        globalSettings.KIOSK_VOTING_PORTAL_URL,
        globalSettings.ONLINE_VOTING_CLIENT_ID,
    ]);
    useEffect(function () {
        function updateTokenPeriodically() {
            return __awaiter(this, void 0, void 0, function () {
                var tokenLifespan, bufferSecs, minValidity, refreshed;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            tokenLifespan = globalSettings.KEYCLOAK_ACCESS_TOKEN_LIFESPAN_SECS;
                            bufferSecs = 10;
                            minValidity = tokenLifespan - bufferSecs;
                            if (!keycloak) return [3 /*break*/, 2];
                            return [4 /*yield*/, keycloak.updateToken(minValidity)];
                        case 1:
                            refreshed = _a.sent();
                            if (refreshed) {
                                setKeycloakAccessToken(keycloak.token);
                            }
                            _a.label = 2;
                        case 2: return [4 /*yield*/, sleep(tokenLifespan * 1e3)];
                        case 3:
                            _a.sent();
                            updateTokenPeriodically();
                            return [2 /*return*/];
                    }
                });
            });
        }
        function initializeKeycloak() {
            return __awaiter(this, void 0, void 0, function () {
                var rawLocale, keycloakInitOptions, isAuthenticatedResponse, baseUrl, queryString, registerOptions, registerUrl, loginUrl, error_1;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            if (!keycloak) {
                                return [2 /*return*/];
                            }
                            _a.label = 1;
                        case 1:
                            _a.trys.push([1, 11, , 12]);
                            rawLocale = getLanguageFromURL() ||
                                getValueFromCookie(USER_LANGUAGE_COOKIE_NAME) ||
                                defaultLocale;
                            keycloakInitOptions = {
                                // Configure that Keycloak will check if a user is already authenticated (when
                                // opening the app or reloading the page). If not authenticated the user will
                                // be send to the login form. If already authenticated the webapp will open.
                                checkLoginIframe: false,
                                // Keycloak expects BCP 47 locale codes (e.g. "ca" for Catalan, not "cat")
                                locale: rawLocale ? toBCP47(rawLocale) : undefined,
                            };
                            return [4 /*yield*/, keycloak.init(keycloakInitOptions)
                                // If the authentication was not successfull the user is send back to the Keycloak login form
                            ];
                        case 2:
                            isAuthenticatedResponse = _a.sent();
                            if (!(!isAuthenticatedResponse && authType)) return [3 /*break*/, 10];
                            if (!(authType === "register")) return [3 /*break*/, 6];
                            baseUrl = window.location.origin + window.location.pathname;
                            queryString = window.location.search;
                            registerOptions = __assign(__assign({}, keycloakInitOptions), { 
                                // after successful enrollment, we should redirect to login
                                redirectUri: enrollmentRedirectUrl(baseUrl, queryString), loginHint: loginHints.username });
                            if (!(Object.keys(loginHints).length > 0)) return [3 /*break*/, 4];
                            return [4 /*yield*/, keycloak.createRegisterUrl(registerOptions)];
                        case 3:
                            registerUrl = _a.sent();
                            window.location.assign(appendLoginHints(registerUrl, loginHints));
                            return [2 /*return*/];
                        case 4: return [4 /*yield*/, keycloak.register(registerOptions)];
                        case 5: return [2 /*return*/, _a.sent()];
                        case 6:
                            if (!(Object.keys(loginHints).length > 0)) return [3 /*break*/, 8];
                            return [4 /*yield*/, keycloak.createLoginUrl(__assign(__assign({}, keycloakInitOptions), { 
                                    // Stock username forms only understand the standard OIDC hint.
                                    loginHint: loginHints.username }))];
                        case 7:
                            loginUrl = _a.sent();
                            window.location.assign(appendLoginHints(loginUrl, loginHints));
                            return [2 /*return*/];
                        case 8: return [4 /*yield*/, keycloak.login(keycloakInitOptions)];
                        case 9: return [2 /*return*/, _a.sent()];
                        case 10:
                            if (!keycloak.token) {
                                setAuthenticated(false);
                                return [2 /*return*/];
                            }
                            setAuthenticated(true);
                            setIsKeycloakInitialized(true);
                            setKeycloakAccessToken(keycloak.token);
                            updateTokenPeriodically();
                            return [3 /*break*/, 12];
                        case 11:
                            error_1 = _a.sent();
                            console.log("error initializing Keycloak");
                            console.log(error_1);
                            setAuthenticated(false);
                            return [3 /*break*/, 12];
                        case 12: return [2 /*return*/];
                    }
                });
            });
        }
        if (keycloak && !isAuthenticated && !isKeycloakInitialized) {
            initializeKeycloak();
        }
    }, [keycloak, isAuthenticated, isKeycloakInitialized, authType, defaultLocale, loginHints]);
    /**
     * Returns true only if the JWT has gold permissions and the JWT
     * authentication is fresh, i.e. performed less than 60 seconds ago.
     */
    // TODO: This is duplicated from jwt.rs in sequent-core, we should just use
    // the same WASM function if possible
    var isGoldUser = function () {
        var _a, _b, _c, _d;
        var acr = (_b = (_a = keycloak === null || keycloak === void 0 ? void 0 : keycloak.tokenParsed) === null || _a === void 0 ? void 0 : _a.acr) !== null && _b !== void 0 ? _b : null;
        var isGold = acr === IPermissions.GOLD_PERMISSION;
        var authTimeTimestamp = (_d = (_c = keycloak === null || keycloak === void 0 ? void 0 : keycloak.tokenParsed) === null || _c === void 0 ? void 0 : _c.auth_time) !== null && _d !== void 0 ? _d : 0;
        var authTime = new Date(authTimeTimestamp * 1000);
        var freshnessLimit = new Date(Date.now().valueOf() - 60 * 1000);
        var isFresh = authTime > freshnessLimit;
        return isGold && isFresh;
    };
    var reauthWithGold = function (redirectUri) { return __awaiter(void 0, void 0, Promise, function () {
        var error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, (keycloak === null || keycloak === void 0 ? void 0 : keycloak.login({
                            acr: { essential: true, values: [IPermissions.GOLD_PERMISSION] },
                            redirectUri: redirectUri || window.location.href, // Use the passed URL or fallback to current URL
                            locale: toBCP47(i18n.language),
                        }))];
                case 1:
                    _a.sent();
                    return [3 /*break*/, 3];
                case 2:
                    error_2 = _a.sent();
                    console.error("Re-authentication failed:", error_2);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    useEffect(function () {
        function loadProfile() {
            return __awaiter(this, void 0, void 0, function () {
                var profile_1, newTenantId, _a;
                var _b;
                return __generator(this, function (_c) {
                    switch (_c.label) {
                        case 0:
                            if (!keycloak) {
                                return [2 /*return*/];
                            }
                            _c.label = 1;
                        case 1:
                            _c.trys.push([1, 3, , 4]);
                            return [4 /*yield*/, keycloak.loadUserProfile()];
                        case 2:
                            profile_1 = _c.sent();
                            setUserProfile(function (val) { return (__assign(__assign({}, val), { userId: (profile_1 === null || profile_1 === void 0 ? void 0 : profile_1.id) || (val === null || val === void 0 ? void 0 : val.userId), email: (profile_1 === null || profile_1 === void 0 ? void 0 : profile_1.email) || (val === null || val === void 0 ? void 0 : val.email), firstName: (profile_1 === null || profile_1 === void 0 ? void 0 : profile_1.firstName) || (val === null || val === void 0 ? void 0 : val.firstName), username: (profile_1 === null || profile_1 === void 0 ? void 0 : profile_1.username) || (val === null || val === void 0 ? void 0 : val.username) })); });
                            newTenantId = (_b = profile_1 === null || profile_1 === void 0 ? void 0 : profile_1.attributes["tenant-id"]) === null || _b === void 0 ? void 0 : _b[0];
                            if (newTenantId) {
                                setTenantId(newTenantId);
                            }
                            return [3 /*break*/, 4];
                        case 3:
                            _a = _c.sent();
                            console.log("error trying to load the users profile");
                            return [3 /*break*/, 4];
                        case 4: return [2 /*return*/];
                    }
                });
            });
        }
        // Only load the profile if a user is authenticated
        if (keycloak && isAuthenticated && isKeycloakInitialized) {
            loadProfile();
        }
    }, [keycloak, isAuthenticated, isKeycloakInitialized]);
    var setTenantEvent = function (tenantId, eventId, authType, defaultLocale, initialLoginHints) {
        setTenantId(tenantId);
        setEventId(eventId);
        setDefaultLocale(defaultLocale);
        authType && setAuthType(authType);
        setLoginHints(initialLoginHints !== null && initialLoginHints !== void 0 ? initialLoginHints : {});
    };
    var getRedirectUrl = function (redirectUrl) {
        return getLogoutRedirectUrl({
            redirectUrl: redirectUrl,
            origin: window.location.origin,
            pathname: window.location.pathname,
            tenantId: tenantId,
            eventId: eventId,
            clientId: keycloak === null || keycloak === void 0 ? void 0 : keycloak.clientId,
            defaultClientId: globalSettings.ONLINE_VOTING_CLIENT_ID,
            hasKioskQuery: new URLSearchParams(window.location.search).has("kiosk"),
        });
    };
    var logout = function (redirectUrl) {
        if (!keycloak) {
            // If no keycloak object initailized manually clear cookies and redirect user
            clearAllCookies();
            window.location.href = getRedirectUrl(redirectUrl);
            return;
        }
        keycloak.logout({
            redirectUri: getRedirectUrl(redirectUrl),
        });
    };
    var clearAllCookies = function () {
        document.cookie.split(";").forEach(function (cookie) {
            var eqPos = cookie.indexOf("=");
            var name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
        });
    };
    /**
     * Check if the user has the given role
     * @param role to be checked
     * @returns whether or not if the user has the role
     */
    var hasRole = function (role) {
        if (!keycloak) {
            return false;
        }
        return keycloak.hasRealmRole(role);
    };
    /**
     * Check if the user is in kiosk mode
     * @returns whether or not the user in kiosk mode
     */
    var isKiosk = function () {
        var _a, _b;
        var clientId = (_a = keycloak === null || keycloak === void 0 ? void 0 : keycloak.clientId) !== null && _a !== void 0 ? _a : (_b = keycloak === null || keycloak === void 0 ? void 0 : keycloak.tokenParsed) === null || _b === void 0 ? void 0 : _b.azp;
        return isKioskClientId(clientId, globalSettings.ONLINE_VOTING_CLIENT_ID);
    };
    var openProfileLink = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!keycloak) {
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, keycloak.accountManagement()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    var getExpiry = function () {
        var _a;
        var exp = (_a = keycloak === null || keycloak === void 0 ? void 0 : keycloak.tokenParsed) === null || _a === void 0 ? void 0 : _a.exp;
        return exp ? new Date(exp * 1000) : undefined;
    };
    // Setup the context provider
    return (_jsx(AuthContext.Provider, { value: {
            isAuthContextInitialized: isKeycloakInitialized,
            isAuthenticated: isAuthenticated,
            userId: (_a = userProfile === null || userProfile === void 0 ? void 0 : userProfile.userId) !== null && _a !== void 0 ? _a : "",
            username: (_b = userProfile === null || userProfile === void 0 ? void 0 : userProfile.username) !== null && _b !== void 0 ? _b : "",
            email: (_c = userProfile === null || userProfile === void 0 ? void 0 : userProfile.email) !== null && _c !== void 0 ? _c : "",
            firstName: (_d = userProfile === null || userProfile === void 0 ? void 0 : userProfile.firstName) !== null && _d !== void 0 ? _d : "",
            setTenantEvent: setTenantEvent,
            getExpiry: getExpiry,
            logout: logout,
            hasRole: hasRole,
            isKiosk: isKiosk,
            openProfileLink: openProfileLink,
            keycloakAccessToken: keycloakAccessToken,
            isGoldUser: isGoldUser,
            reauthWithGold: reauthWithGold,
        }, children: props.children }));
};
export default AuthContextProvider;
